import React from 'react';
import { Plugins } from '@leverege/plugin';
import { ValueCache } from '@leverege/value-cache';
import Model from './DetailModel.js';
import Attributes from '../../attribute/Attributes.js';
import Formatters from '../../attribute/formatter/Formatters.js';
import I18nResolvers from '../../attribute/i18nResolver/I18nResolvers.js';
import { LabelText, ValueText, SuffixText } from "./DetailText.js";
import Util from './Util.js';
import InlineEditor from "../../attribute/editor/InlineEditor.js";
import Visibilizers from '../../visibilizer/Visibilizers.js';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY = {};
const USAGE = 'detail';

/**
 * This is a generic detail renderer for an Attribute. It will render the label
 * and the first value in the returned formatter array. To use, either derive
 * from this renderer or install it directly in the Plugins against the detail name.
 *
 *  Plugins.add( 'DetailRenderer', {
 *    type : Util.getDetailTypeForType( 'MyType' ),
 *    renderer : AttributeDetailRenderer } )
 */
export default class AttributeDetailRenderer {
  constructor(detail) {
    this.detail = detail;
    this.name = detail.attrName;
    this.valueRendererCache = new ValueCache();
  }
  getContext(object, cxt) {
    return {
      ...cxt,
      renderer: this,
      target: [object],
      usage: USAGE
    };
  }
  filterRenderersByType = renderer => {
    return renderer.objectType === this.detail.objectType;
  };
  getValueRenderer = (renderers, context) => {
    return (value, valueStyle, object, context) => {
      const {
        value: valueOpts = EMPTY
      } = this.detail;
      const cxt = this.getContext(object, context);
      const valueRenderer = renderers.find(rnd => {
        return this.detail.attrName === rnd.attrName && (rnd.handles == null || rnd.handles && rnd.handles(cxt));
      });
      const valueVariant = Util.getVariant(valueOpts, object, {
        context: cxt,
        value,
        detailModel: this.detail,
        usage: USAGE
      });
      return valueRenderer ? valueRenderer.renderer(value, valueStyle, object, cxt, {
        valueVariant
      }) : this.getValueComponent(value, valueStyle, object, cxt, {
        valueVariant
      });
    };
  };
  getLabelAndValue(object, cxt) {
    const attr = Attributes.getAttribute(this.name, object?.type);
    const label = this.detail?.label?.name || attr?.displayName || this.name || '';
    const value = attr == null ? null : attr.get(object.data, cxt);
    return [label, value];
  }

  /**
   * Returns a component to be rendered as the value
   */
  getValueComponent(value, valueStyle, object, cxt, {
    valueVariant
  }) {
    const {
      editable
    } = cxt;
    const context = this.getContext(object, cxt);
    if (this.detail.formatter) {
      context.formatter = this.detail.formatter;
    }
    return /*#__PURE__*/_jsx(InlineEditor, {
      usage: "detail",
      variant: valueVariant,
      editable: editable,
      attrName: this.name,
      object: object,
      style: valueStyle,
      context: context,
      children: /*#__PURE__*/_jsx("div", {
        children: Array.isArray(value) && value.map((entry, index, array) => {
          if (index % 2 === 1) {
            return null;
          }
          const suffix = array.length > index + 1 ? array[index + 1] : null;
          if (valueVariant) {
            return /*#__PURE__*/_jsx(ValueText, {
              variant: valueVariant,
              style: valueStyle,
              children: `${entry}${suffix != null ? suffix : ''}`
            });
          }
          return /*#__PURE__*/_jsxs("div", {
            style: {
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap'
            },
            children: [/*#__PURE__*/_jsx(ValueText, {
              variant: valueVariant,
              style: valueStyle,
              children: entry
            }), suffix && /*#__PURE__*/_jsx(SuffixText, {
              style: valueStyle,
              children: suffix
            })]
          }, entry);
        })
      })
    }, this.name);
  }

  /**
   * Returns a component to be rendered as the value
   */
  getLabelComponent(label, labelStyle, labelVariant) {
    return /*#__PURE__*/_jsx(LabelText, {
      style: labelStyle,
      variant: labelVariant,
      children: label
    });
  }
  getComponents(object, cxt = EMPTY) {
    const {
      label: labelOpts = EMPTY,
      value: valueOpts = EMPTY,
      background
    } = this.detail;
    const {
      editable
    } = cxt;
    // eslint-disable-next-line prefer-const
    let [label, value] = this.getLabelAndValue(object, cxt);
    value = this.formatAndI18nResolveValue(value);
    const isVisible = Visibilizers.getIsVisible(Model.getValueVisible(this.detail), object, {
      ...cxt,
      attrName: this.detail.attrName,
      value,
      model: this.detail
    });
    if (!isVisible) {
      return null;
    }
    const hasValue = !(value == null || value.length === 0);
    if (!hasValue) {
      value = [valueOpts.emptyValue];
    }
    const bg = Util.getBackground(background, object, cxt);
    const lps = Util.getParentStyle(labelOpts, bg, object, cxt);
    const vps = Util.getParentStyle(valueOpts, bg, object, cxt);
    const ls = Util.getStyle(labelOpts, object, cxt);
    const vs = Util.getStyle(valueOpts, object, cxt);
    const lv = Util.getVariant(labelOpts, object, {
      context: cxt,
      detailModel: this.detail,
      usage: USAGE
    });
    const renderers = Plugins.createView('AttributeValueRenderer', {
      name: this.detail.objectType,
      filter: this.filterRenderersByType
    }).get();
    const valueRenderer = this.valueRendererCache.calc(this.getValueRenderer, renderers, cxt);
    value = valueRenderer(value, vs, object, cxt);
    if (labelOpts.visible === false && !editable) {
      return [{
        component: value,
        style: vps
      }];
    }
    return [{
      component: this.getLabelComponent(label, ls, lv),
      style: lps
    }, {
      component: value,
      style: vps
    }];
  }
  formatAndI18nResolveValue(value) {
    const formattedValue = Formatters.format(value, this.detail.formatter);
    return I18nResolvers.resolve(formattedValue, this.detail);
  }
}